import { useEffect } from "react";
import { UseFormSetValue} from "react-hook-form";

interface TotalSumProps {
  tag?: string
  id: string
  setValue: UseFormSetValue<Record<string,string>>
}

/**
 * Sums all input values inside divs with class '<type>_total' and sets the
 * summed value on the field tagged '<type>_total_sum'. For '<type>_consumption'
 * the value is instead '<type>_total_yesterday' minus that sum.
 * Only the target field runs the summing - group members return early.
 */
export const useSakuraTotalSum = ({tag, id, setValue}:  TotalSumProps) => {

  useEffect(() => {
    if (!tag) return;

    const ops = ["consumption", "total"]
    const splitTag = tag.includes("_") ? tag.split("_") : null
    const totalType = ops.includes(splitTag?.[1] ?? "") ? splitTag?.[0] : null
    if (!totalType) return;

    // Only the sum/consumption field writes a total
    const isConsumption = tag === `${totalType}_consumption`;
    if (tag !== `${totalType}_total_sum` && !isConsumption) return;

    const parseVal = (value?: string | null) => {
      const val = parseFloat(String(value).replace(",", "."));
      return isNaN(val) ? 0 : val;
    }

    const sum = () =>
      Array.from(
          document.querySelectorAll<HTMLInputElement>(
            `div.${totalType}_total > input`,
          ),
        ).reduce((acc, input) => {
          const val = parseVal(input.value);
          return acc + val;
        }, 0)

    const getYesterdayValue = () => {
      const element = document.getElementById(`${totalType}_total_yesterday`);
      const input =
        element instanceof HTMLInputElement
          ? element
          : element?.querySelector("input") ?? null;

      return parseVal(input?.value);
    };

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const sumAndSet = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const total = isConsumption ? getYesterdayValue() - sum() : sum();
        setValue(id, Number.isFinite(total) ? total.toString() : "");
      }, 0);
    };

    sumAndSet(); // initial sum

    // Attach blur event listeners on all inputs inside div.<type>_total
    const inputs = Array.from(
      document.querySelectorAll<HTMLInputElement>(
        `div.${totalType}_total > input`,
      ),
    );
    inputs.forEach(input => input.addEventListener("blur", sumAndSet));

    return () => {
      clearTimeout(timeoutId);
      inputs.forEach(input => input.removeEventListener("blur", sumAndSet));
    };
  }, [tag, id, setValue]);
};
